import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Github, Mail, Globe, FileText, Tag } from 'lucide-react';
import { api } from '../api';
import { Markdown } from '../components/Markdown';
import { parseProfile } from '../config/profile';

export function ProfilePage() {
  const { data: page, isLoading } = useQuery({
    queryKey: ['page', 'profile'],
    queryFn: () => api.getPage('profile'),
  });

  const { data: cardPage } = useQuery({
    queryKey: ['page', 'profile-card'],
    queryFn: () => api.getPage('profile-card'),
  });

  const { data: archive } = useQuery({
    queryKey: ['archive'],
    queryFn: () => api.getArchive(),
  });

  const { data: tags } = useQuery({
    queryKey: ['tags'],
    queryFn: () => api.listTags(),
  });

  const profile = parseProfile(cardPage?.content_md);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="mx-auto max-w-5xl px-4 pb-20 pt-24 sm:px-6 lg:px-8"
    >
      <div className="grid gap-10 lg:grid-cols-[300px_1fr]">
        {/* 名片卡片 */}
        <aside>
          <div className="sticky top-24 rounded-2xl border border-stone-200 bg-white p-6 dark:border-indigo-500/20 dark:bg-space-800/60">
            {profile.avatar_url ? (
              <img
                src={profile.avatar_url}
                alt={profile.name}
                className="mb-5 h-32 w-32 rounded-full object-cover ring-4 ring-indigo-500/20"
              />
            ) : (
              <div className="mb-5 flex h-32 w-32 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 text-5xl font-bold text-white ring-4 ring-indigo-500/20">
                {profile.name.charAt(0).toUpperCase()}
              </div>
            )}

            <h1 className="text-2xl font-bold text-stone-900 dark:text-star">{profile.name}</h1>
            <p className="mt-2 text-sm text-stone-600 dark:text-star-dim">{profile.bio}</p>

            <div className="mt-5 flex gap-4 border-t border-stone-100 pt-5 text-sm text-stone-600 dark:border-indigo-500/10 dark:text-star-dim">
              <span className="flex items-center gap-1.5">
                <FileText size={14} className="text-indigo-500 dark:text-nebula-glow" />
                {archive?.length ?? 0} 文章
              </span>
              <span className="flex items-center gap-1.5">
                <Tag size={14} className="text-indigo-500 dark:text-nebula-glow" />
                {tags?.length ?? 0} 标签
              </span>
            </div>

            <div className="mt-5 space-y-3 border-t border-stone-100 pt-5 dark:border-indigo-500/10">
              {profile.github && (
                <a
                  href={profile.github}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-3 text-sm text-stone-600 transition-colors hover:text-indigo-600 dark:text-star-dim dark:hover:text-nebula-glow"
                >
                  <Github size={16} />
                  GitHub
                </a>
              )}
              {profile.email && (
                <a
                  href={`mailto:${profile.email}`}
                  className="flex items-center gap-3 text-sm text-stone-600 transition-colors hover:text-indigo-600 dark:text-star-dim dark:hover:text-nebula-glow"
                >
                  <Mail size={16} />
                  {profile.email}
                </a>
              )}
              {profile.website && (
                <a
                  href={profile.website}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-3 text-sm text-stone-600 transition-colors hover:text-indigo-600 dark:text-star-dim dark:hover:text-nebula-glow"
                >
                  <Globe size={16} />
                  {profile.website.replace(/^https?:\/\//, '')}
                </a>
              )}
            </div>
          </div>
        </aside>

        {/* 正文 */}
        <div>
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-6 animate-pulse rounded bg-stone-200 dark:bg-space-800"></div>
              ))}
            </div>
          ) : page?.content_md ? (
            <article className="prose prose-lg max-w-none dark:prose-invert">
              <Markdown content={page.content_md} />
            </article>
          ) : (
            <p className="py-20 text-center text-stone-500 dark:text-star-dim">
              还没有内容，去后台「个人主页」写点什么吧。
            </p>
          )}
        </div>
      </div>
    </motion.div>
  );
}
